import MultiSelectDropdown from 'components/ReusableComponents/MultiSelectDropdown'
import { hotelStatusNamesFilterable } from 'main/hotelStatuses'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import userApi from 'services/api/user'

import Dropdown from '../../../../components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from '../../../../components/ReusableComponents/ManageHotel/TextInput'
import HealthCheckSearchableSelect from '../HealthCheckSearchableSelect'
import SmartDatepicker, {
  smartDateTypes
} from '../../../../components/ReusableComponents/SmartDatepicker'
import Toggle from '../../../../components/ReusableComponents/Toggle/index'
import { parentChainNames, PMSnames } from '../../../../main/hotelBrandsAndChains'
import filterStyles from '../Filters.module.scss'

const { last7days, last30days } = smartDateTypes

let csmDataLoadedForSession = false
let globalCsmDataCache = null

function generateDateString(period) {
  const lastDates = `Last ${period} days`

  const range = `(${moment()
    .add(-1 - period, 'days')
    .format('D MMM')} -
    ${moment().add(-1, 'days').format('D MMM')})`

  return lastDates + ' ' + range
}

const HealthCheckFilters = ({
  dispatch,
  state,
  showTelcoToggle,
  showLowOutreachToggle,
  showLowConversionsToggle,
  showHideZeroBalanceToggle,
  showHideInvalidPaymentDateToggle,
  showPaymentDateFilter,
  showSubsidiaryStatus,
  showPMSFilter,
  showDateFilter,
  showCsmFilter,
  showInviteStatusFilter,
  allhotelGroups,
  allhotelChains,
  children
}) => {
  const {
    showTelcoDetails,
    showLowOutreaches,
    showLowConversions,
    hideZeroBalance,
    hideInvalidPaymentDate,
    paymentDateFrom,
    paymentDateTo,
    hotelGroups,
    chains,
    brands,
    pms,
    searchString,
    timeperiod,
    subsidiaryStatus,
    csmIds,
    inviteStatusFilter
  } = state

  const { disconnected, ...pmsNamesExceptDisconnected } = PMSnames // eslint-disable-line no-unused-vars

  const csmUserId = useSelector(state => state.login.csmUserId)

  const [csmData, setCsmData] = useState([])
  const [searchDraft, setSearchDraft] = useState(searchString || '')
  const searchInputRef = useRef(null)
  const searchDebounceTimerRef = useRef(null)

  /** Only apply default "my CSM" once on first paint; do not reset when user clears the filter. */
  const csmDefaultAppliedRef = useRef(false)

  // Memoize onChange handlers to prevent infinite loops
  const handleStartDateChange = useCallback(
    date => {
      const dateString = date ? date.format('YYYY-MM-DD') : null
      // Only dispatch if the value actually changed to prevent loops
      if (dateString !== paymentDateFrom) {
        dispatch({ type: 'paymentDateFrom', payload: dateString })
      }
    },
    [paymentDateFrom, dispatch]
  )

  const handleEndDateChange = useCallback(
    date => {
      const dateString = date ? date.format('YYYY-MM-DD') : null
      // Only dispatch if the value actually changed to prevent loops
      if (dateString !== paymentDateTo) {
        dispatch({ type: 'paymentDateTo', payload: dateString })
      }
    },
    [paymentDateTo, dispatch]
  )

  const handleSearchChange = useCallback(
    e => {
      const nextValue = e.target.value
      setSearchDraft(nextValue)
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current)
      }
      searchDebounceTimerRef.current = setTimeout(() => {
        dispatch({ type: 'searchString', payload: nextValue })
      }, 250)
      window.requestAnimationFrame(() => {
        const input = searchInputRef.current
        if (!input) {
          return
        }
        input.focus()
        const end = String(nextValue).length
        if (typeof input.setSelectionRange === 'function') {
          input.setSelectionRange(end, end)
        }
      })
    },
    [dispatch]
  )

  useEffect(() => {
    setSearchDraft(searchString || '')
  }, [searchString])

  useEffect(() => {
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current)
      }
    }
  }, [])


  useEffect(() => {
    if (showCsmFilter) {
      if (globalCsmDataCache) {
        setCsmData(globalCsmDataCache);
      } else if (!csmDataLoadedForSession) {
        csmDataLoadedForSession = true
        userApi
          .getCustomerSuccessManagers()
          .then(data => {
            // Ensure data is an array before setting
            if (Array.isArray(data)) {
              globalCsmDataCache = data;
              setCsmData(data)
            } else {
              console.warn('CSM data is not an array:', data)
              setCsmData([]) // Set empty array as fallback
            }
          })
          .catch(error => {
            console.error('Failed to fetch CSM data:', error)
            // Set empty array to prevent UI from breaking
            setCsmData([])
            csmDataLoadedForSession = false
          })
      }
    }
  }, [showCsmFilter])


  useEffect(() => {
    if (!showCsmFilter || !csmUserId || csmDefaultAppliedRef.current) {
      return
    }
    csmDefaultAppliedRef.current = true
    if (!csmIds || csmIds.length === 0) {
      dispatch({
        type: 'csmIds',
        payload: [`${csmUserId}`]
      })
    }
  }, [dispatch, csmUserId, csmIds, showCsmFilter])

  return (
    <div className={filterStyles.container}>
      <div className={filterStyles.filterStack}>
        <div className={filterStyles.filterRow} aria-label="Hotel scope filters">
          <div className={[filterStyles.fieldGroup, filterStyles.fieldGroupHotel].join(' ')}>
            <span className={filterStyles.fieldLabel}>Hotel group</span>
            <HealthCheckSearchableSelect
              value={hotelGroups}
              className={[filterStyles.dropdown, filterStyles.fieldHotel].join(' ')}
              onChange={v => dispatch({ type: 'hotelGroups', payload: v })}
              options={{
                all: 'All hotel groups',
                ...allhotelGroups
              }}
            />
          </div>
          <div className={[filterStyles.fieldGroup, filterStyles.fieldGroupHotel].join(' ')}>
            <span className={filterStyles.fieldLabel}>Brand</span>
            <HealthCheckSearchableSelect
              className={[filterStyles.dropdown, filterStyles.fieldHotel].join(' ')}
              value={brands}
              onChange={v => dispatch({ type: 'brands', payload: v })}
              options={{
                all: 'All brands',
                ...parentChainNames
              }}
            />
          </div>
          <div className={[filterStyles.fieldGroup, filterStyles.fieldGroupHotel].join(' ')}>
            <span className={filterStyles.fieldLabel}>Chain</span>
            <HealthCheckSearchableSelect
              className={[filterStyles.dropdown, filterStyles.fieldHotel].join(' ')}
              value={chains}
              onChange={v => dispatch({ type: 'chains', payload: v })}
              options={{
                all: 'All chains',
                ...allhotelChains
              }}
            />
          </div>
        </div>
        <div className={filterStyles.filterRow} aria-label="PMS and list filters">
          {showPMSFilter && (
            <div className={[filterStyles.fieldGroup, filterStyles.fieldGroupScope].join(' ')}>
              <span className={filterStyles.fieldLabel}>PMS</span>
              <div className={filterStyles.fieldScope}>
                <MultiSelectDropdown
                  className={filterStyles.statusDropdown}
                  itemName="PMS"
                  value={pms}
                  hasUnselectButton
                  searchable
                  onChangeValue={values => dispatch({ type: 'pms', payload: values })}
                  options={pmsNamesExceptDisconnected}
                />
              </div>
            </div>
          )}
          {showSubsidiaryStatus && (
            <div className={[filterStyles.fieldGroup, filterStyles.fieldGroupScope].join(' ')}>
              <span className={filterStyles.fieldLabel}>Status</span>
              <div className={filterStyles.fieldScope}>
                <MultiSelectDropdown
                  className={filterStyles.statusDropdown}
                  options={hotelStatusNamesFilterable}
                  value={subsidiaryStatus}
                  hasUnselectButton
                  searchable
                  onChangeValue={statuses =>
                    dispatch({ type: 'subsidiaryStatus', payload: statuses })
                  }
                  itemName="statuses"
                />
              </div>
            </div>
          )}
          {showDateFilter && (
            <div className={[filterStyles.fieldGroup, filterStyles.fieldGroupDate].join(' ')}>
              <span className={filterStyles.fieldLabel}>Time period</span>
              <Dropdown
                className={[filterStyles.dropdown, filterStyles.wideDropdown, filterStyles.fieldDate].join(
                  ' '
                )}
                value={String(timeperiod)}
                onChange={e =>
                  dispatch({ type: 'timePeriod', payload: Number(e.target.value) })
                }
                options={{
                  1: generateDateString(1),
                  7: generateDateString(7),
                  14: generateDateString(14),
                  30: generateDateString(30)
                }}
              />
            </div>
          )}
          {showCsmFilter && (
            <div className={[filterStyles.fieldGroup, filterStyles.fieldGroupScope].join(' ')}>
              <span className={filterStyles.fieldLabel}>CSM</span>
              <div className={filterStyles.fieldScope}>
                <MultiSelectDropdown
                  className={filterStyles.statusDropdown}
                  options={Object.fromEntries(csmData.map(csm => [csm.id, csm.fullName]))}
                  value={csmIds}
                  hasUnselectButton
                  searchable
                  onChangeValue={value => dispatch({ type: 'csmIds', payload: value })}
                  itemName="CSM"
                  itemNamePlural="CSMs"
                />
              </div>
            </div>
          )}
          {showPaymentDateFilter && (
            <div className={[filterStyles.fieldGroup, filterStyles.fieldGroupDate].join(' ')}>
              <span className={filterStyles.fieldLabel}>Payment dates</span>
              <SmartDatepicker
                localstorageName="health-check-payment-date"
                isRange={true}
                startDate={
                  paymentDateFrom ? moment(paymentDateFrom) : moment().subtract(30, 'days')
                }
                endDate={paymentDateTo ? moment(paymentDateTo) : moment()}
                onChangeStartDate={handleStartDateChange}
                onChangeEndDate={handleEndDateChange}
                smartDates={[last7days, last30days]}
                showMonths={false}
                showQuarters={false}
                className={[filterStyles.datepicker, filterStyles.fieldDate].join(' ')}
                dismissCurrentDay={true}
              />
            </div>
          )}
          {showInviteStatusFilter && (
            <div className={[filterStyles.fieldGroup, filterStyles.fieldGroupInvite].join(' ')}>
              <span className={filterStyles.fieldLabel}>Invite status</span>
              <Dropdown
                className={[filterStyles.dropdown, filterStyles.fieldInvite].join(' ')}
                value={inviteStatusFilter || 'all'}
                onChange={e =>
                  dispatch({ type: 'inviteStatusFilter', payload: e.target.value })
                }
                options={{
                  all: 'All invite statuses',
                  1: 'Last 1 day',
                  Lost3Months: '3 Mo',
                  Lost6Months: '6 Mo',
                  Lost9Months: '9 Mo',
                  Lost12Months: '12 Mo',
                  Lost15Months: '15 Mo',
                  Lost15MonthsPlus: '15 Mo+',
                  AfterLastVisit: 'After visit'
                }}
              />
            </div>
          )}
          <div className={[filterStyles.fieldGroup, filterStyles.fieldGroupSearch].join(' ')}>
            <span className={filterStyles.fieldLabel}>Table search</span>
            <div className={filterStyles.filterSearchWrapInner}>
              <TextInput
                type="search"
                className={filterStyles.searchInput}
                value={searchDraft}
                onChange={handleSearchChange}
                forwardedRef={searchInputRef}
                placeholder="Hotel name, ID, or DisplayId (e.g. 489)"
              />
            </div>
          </div>
        </div>
      </div>
      <div className={filterStyles.toggleContainer}>
        {showTelcoToggle && (
          <Toggle
            label="Show telecom details"
            checked={showTelcoDetails}
            handleChange={e =>
              dispatch({ type: 'showTelcoDetails', payload: e.target.checked })
            }
            className={filterStyles.lowResultToggle}
          />
        )}
        {showLowOutreachToggle && (
          <Toggle
            label="Low outreaches"
            checked={showLowOutreaches}
            handleChange={e =>
              dispatch({ type: 'showLowOutreaches', payload: e.target.checked })
            }
            className={filterStyles.lowResultToggle}
          />
        )}
        {showLowConversionsToggle && (
          <Toggle
            label="Low conversions"
            checked={showLowConversions}
            handleChange={e =>
              dispatch({ type: 'showLowConversions', payload: e.target.checked })
            }
            className={filterStyles.lowResultToggle}
          />
        )}
        {showHideZeroBalanceToggle && (
          <Toggle
            label="Hide zero balance"
            checked={hideZeroBalance}
            handleChange={e =>
              dispatch({ type: 'hideZeroBalance', payload: e.target.checked })
            }
            className={filterStyles.lowResultToggle}
          />
        )}
        {showHideInvalidPaymentDateToggle && (
          <Toggle
            label="Hide invalid payment date"
            checked={hideInvalidPaymentDate}
            handleChange={e =>
              dispatch({ type: 'hideInvalidPaymentDate', payload: e.target.checked })
            }
            className={filterStyles.lowResultToggle}
          />
        )}
        {children}
      </div>
    </div>
  )
}

export default HealthCheckFilters
